import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { courseAPI } from '../api/courseApi';
import { useAuth } from '../context/AuthContext';
import { getImageUrl, formatPrice } from '../utils/helpers';
import Loader from '../components/Loader';
import PaymentDialog from '../components/PaymentDialog';
import { toast } from 'react-toastify';

const CourseDetails = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useAuth();
  const [loading, setLoading] = useState(true);
  const [courseData, setCourseData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  
  // Get flag from navigation state (matching mobile app)
  // flag: 0 = purchased view (no buy buttons), flag: 1 = shopping view (show buy buttons)
  const flag = location.state?.flag !== undefined ? location.state.flag : 1;

  useEffect(() => {
    fetchCourseDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, userInfo]);

  const fetchCourseDetails = async () => {
    try {
      setLoading(true);
      const userId = userInfo?.user_detail?.id || '0';
      
      console.log('Fetching course details:', { courseId, userId, flag });
      
      // Try getSubjectById first (matching mobile app for purchased courses)
      try {
        const subjectResponse = await courseAPI.getSubjectById(courseId);
        console.log('getSubjectById Response:', subjectResponse);
        
        if (subjectResponse && subjectResponse.data && subjectResponse.data.length > 0) {
          setSubjects(subjectResponse.data);
          setCourseData(subjectResponse.data[0]);
          console.log(`Successfully loaded subjects from getSubjectById (flag=${flag})`);
          return;
        }
      } catch (subjectError) {
        console.log('getSubjectById failed, trying courses endpoint:', subjectError);
      }
      
      // Fallback to /courses/{courseId}/{userId} for regular course browsing
      const courseResponse = await courseAPI.getCourseDetails(courseId, userId);
      console.log('getCourseDetails Response:', courseResponse);
      
      if (courseResponse && courseResponse.data && courseResponse.data.length > 0) {
        courseResponse.data.forEach(subject => {
          console.log(`Subject ${subject.id} (${subject.sub_name}): purchased = ${subject.purchased}`);
        });
        setSubjects(courseResponse.data);
        setCourseData(courseResponse.data[0]);
      }
    } catch (error) {
      console.error('Error fetching course details:', error);
      toast.error(error.response?.data?.message || 'Failed to load course details');
    } finally {
      setLoading(false);
    }
  };


  const handleSubjectClick = (subject) => {
    // Check if subject/course is purchased (matching mobile app logic)
    const isPurchased = subject.purchased === 'Credit';
    
    if (!isPurchased) {
      toast.error('Please purchase this course to access the content');
      return;
    }
    
    // Navigate to Subjects page first (matching My Purchases flow)
    navigate(`/subjects/${subject.id}`, {
      state: {
        type: subject.type,
        id: subject.id,
        subject: subject.sub_name || subject.subname,
        flag: 0, // 0 = purchased, has access
        viewType: subject.viewType
      }
    });
  };

  const handleBuyNow = (subject) => {
    if (!userInfo) {
      toast.error('Please login to purchase');
      navigate('/login');
      return;
    }
    setSelectedCourse(subject);
    setShowPaymentDialog(true);
  };

  const handlePaymentSuccess = () => {
    toast.success('Course purchased successfully!');
    setShowPaymentDialog(false);
    // Refresh course details to update purchase status
    fetchCourseDetails();
    // Optionally navigate to my purchases
    setTimeout(() => {
      navigate('/my-purchased');
    }, 2000);
  };

  if (loading) return <Loader />;

  if (!courseData && subjects.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Course not found</h2>
          <button onClick={() => navigate('/courses')} className="btn btn-primary">
            Browse Courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Course Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="container">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            {courseData?.images && (
              <div className="w-full md:w-1/3">
                <img 
                  src={getImageUrl(courseData.images)} 
                  alt={courseData.sub_name || courseData.course_name}
                  className="w-full rounded-lg shadow-2xl"
                />
              </div>
            )}
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                {courseData?.sub_name || courseData?.course_name || 'Course Details'}
              </h1>
              {courseData?.description && (
                <div 
                  className="text-white/90 mb-6"
                  dangerouslySetInnerHTML={{ __html: courseData.description }}
                />
              )}
              {courseData?.price && (
                <div className="flex items-center gap-4 mb-6">
                  {courseData.mrp && (
                    <span className="text-2xl text-white/70 line-through">
                      {formatPrice(courseData.mrp)}
                    </span>
                  )}
                  <span className="text-4xl font-bold text-yellow-300">
                    {formatPrice(courseData.price)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Subjects/Topics List */}
      <div className="py-12">
        <div className="container">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8">
            Course Subjects
          </h2>
          
          {subjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {subjects.map((subject) => {
                const isPurchased = subject.purchased === 'Credit';
                return (
                  <div
                    key={subject.id}
                    className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden ${
                      isPurchased ? 'cursor-pointer' : 'cursor-default'
                    }`}
                    onClick={() => handleSubjectClick(subject)}
                  >
                    {subject.images && (
                      <div className="h-48 overflow-hidden relative">
                        <img 
                          src={getImageUrl(subject.images)}
                          alt={subject.sub_name}
                          className={`w-full h-full object-cover transition-transform duration-300 ${
                            isPurchased ? 'hover:scale-110' : ''
                          }`}
                        />
                        {!isPurchased && (
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <span className="text-white text-5xl">🔒</span>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-3">
                        {subject.sub_name}
                      </h3>
                      {subject.description && (
                        <div 
                          className="text-gray-600 text-sm mb-4 line-clamp-2"
                          dangerouslySetInnerHTML={{ 
                            __html: subject.description.replace(/(&nbsp|amp|quot|lt|gt|;|<([^>]+)>)/gi, '') 
                          }}
                        />
                      )}
                      
                      {/* Show buttons only if flag !== 0 (matching mobile app behavior) */}
                      {flag !== 0 ? (
                        <div className="space-y-3">
                          {subject.purchased === 'Credit' ? (
                            <div className="flex items-center justify-between">
                              <span className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                                ✓ Purchased
                              </span>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSubjectClick(subject);
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition text-sm"
                              >
                                View →
                              </button>
                            </div>
                          ) : (
                            <>
                              {subject.price && (
                                <div className="flex items-center gap-2 mb-2">
                                  {subject.mrp && (
                                    <span className="text-gray-400 line-through text-sm">
                                      ₹{subject.mrp}
                                    </span>
                                  )}
                                  <span className="text-green-600 text-xl font-bold">
                                    ₹{subject.price}
                                  </span>
                                </div>
                              )}
                              <div className="flex gap-2">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBuyNow(subject);
                                  }}
                                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition text-sm"
                                >
                                  Buy Now
                                </button>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSubjectClick(subject);
                                  }}
                                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition text-sm"
                                >
                                  Demo
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-xl">No subjects available for this course.</p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Dialog */}
      <PaymentDialog
        isOpen={showPaymentDialog}
        onClose={() => setShowPaymentDialog(false)}
        courseInfo={selectedCourse}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default CourseDetails;

